<template>
  <section class="booking-emails" aria-label="E-mails" :aria-busy="loading">
    <div class="booking-emails__toolbar">
      <app-button ref="resendButton" @click.native="$modal.show(resendModalName)"><i class="pi pi-envelope" aria-hidden="true" /> Resend E-mail</app-button>
    </div>
    <booking-email-resend-modal :key="bookingId" :name="resendModalName" :booking="booking" @refresh="loadEmails" @closed="focusResendButton" />
    <app-block-loader v-if="loading" min-height="240px" :rows="4">Loading e-mails…</app-block-loader>
    <div v-else-if="error" class="booking-emails__error" role="alert">
      <span>{{ error }}</span>
      <app-button theme="dark" size="small" @click.native="loadEmails">Retry</app-button>
    </div>
    <p v-else-if="!emails.length" class="booking-emails__message">No e-mails yet.</p>
    <ol v-else class="booking-emails__list">
      <li v-for="email in emails" :key="email.id">
        <article class="booking-emails__card" :aria-labelledby="`booking-email-${email.id}`">
          <header class="booking-emails__card-header">
            <h3 :id="`booking-email-${email.id}`"><i class="pi pi-envelope" aria-hidden="true" /> {{ templateName(email) }}</h3>
            <div class="booking-emails__meta">
              <span v-if="email.sendpulse_log" class="booking-emails__sender"><span class="booking-emails__muted">Sender:</span> {{ email.sendpulse_log.sender || '—' }}</span>
              <time v-if="email.sendpulse_log && dateTime(email.sendpulse_log.created_at)" :datetime="email.sendpulse_log.created_at">{{ dateTime(email.sendpulse_log.created_at) }}</time>
              <span v-else class="booking-emails__muted">—</span>
              <span v-if="email.sendpulse_log" class="booking-emails__badge" :data-state="eventState(email.sendpulse_log.status)">{{ acceptance(email.sendpulse_log.status) }}</span>
            </div>
          </header>
          <template v-if="email.sendpulse_log">
            <div class="booking-emails__body">
              <dl class="booking-emails__facts">
                <div class="booking-emails__subject"><dt>Subject:</dt><dd>{{ email.sendpulse_log.subject || '—' }}</dd></div>
              </dl>
            </div>
            <div class="booking-emails__recipients">
              <div v-if="recipients(email).length" class="booking-emails__table-scroll">
                <table>
                  <caption class="booking-emails__sr-only">Recipients of {{ templateName(email) }}</caption>
                  <thead>
                    <tr>
                      <th scope="col">Recipient</th>
                      <th scope="col">Delivered <span class="booking-emails__column-total">{{ total(email.sendpulse_log.recipients_delivered) }} / {{ recipients(email).length }}</span></th>
                      <th scope="col">Delivered at</th>
                      <th scope="col">Opened <span class="booking-emails__column-total">{{ total(email.sendpulse_log.recipients_read) }} / {{ recipients(email).length }}</span></th>
                      <th scope="col">Opened at</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(recipient, index) in recipients(email)" :key="index">
                      <td>{{ recipient.recipient || '—' }}</td>
                      <td><span class="booking-emails__badge" :data-state="eventState(recipient.delivered)">{{ eventLabel(recipient.delivered) }}</span></td>
                      <td><time v-if="dateTime(recipient.delivered_at)" :datetime="recipient.delivered_at">{{ dateTime(recipient.delivered_at) }}</time><span v-else>—</span></td>
                      <td><span class="booking-emails__badge" :data-state="eventState(recipient.opened)">{{ eventLabel(recipient.opened) }}</span></td>
                      <td><time v-if="dateTime(recipient.opened_at)" :datetime="recipient.opened_at">{{ dateTime(recipient.opened_at) }}</time><span v-else>—</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="booking-emails__message">No recipient data available.</p>
            </div>
          </template>
          <p v-else class="booking-emails__message">Email log is not available yet.</p>
        </article>
      </li>
    </ol>
  </section>
</template>

<script>
import { getFlightsBookingEmails } from '@/http'
import BookingEmailResendModal from './BookingEmailResendModal.vue'
import { dateTime, responseError } from './booking-detail'

export default {
  name: 'FlightBookingEmails',
  components: { BookingEmailResendModal },
  props: { booking: { type: Object, required: true }, passengersRefreshKey: { type: Number, default: 0 } },
  data() { return { emails: [], loaded: false, loading: false, error: '', requestId: 0 } },
  computed: { bookingId() { return this.booking.id }, resendModalName() { return `resend-booking-email-${this.bookingId}` } },
  watch: {
    bookingId: { immediate: true, handler() { this.loadEmails() } },
    passengersRefreshKey() { this.loadEmails() }
  },
  beforeDestroy() { this.requestId++ },
  methods: {
    dateTime,
    focusResendButton() { if (this.$refs.resendButton) this.$refs.resendButton.$el.focus() },
    templateName(email) { return (email.sendpulse_template && (email.sendpulse_template.name || email.sendpulse_template.slug)) || 'Email' },
    recipients(email) { return email.sendpulse_log && Array.isArray(email.sendpulse_log.recipients) ? email.sendpulse_log.recipients : [] },
    total(value) { return value === null || value === undefined ? '—' : value },
    eventState(value) { return value === true ? 'yes' : value === false ? 'no' : 'unknown' },
    eventLabel(value) { return value === true ? 'Yes' : value === false ? 'No' : 'Unknown' },
    acceptance(value) { return value === true ? 'Accepted' : value === false ? 'Not accepted' : 'Unknown' },
    async loadEmails() {
      const requestId = ++this.requestId
      const bookingId = this.booking.id
      this.emails = []
      this.loaded = false
      this.error = ''
      this.loading = false
      if (!bookingId) return
      this.loading = true
      try {
        const emails = new Map()
        let page = 1
        let hasNext = true
        while (hasNext) {
          // The API is paginated; read every page for a single list without UI pagination.
          const { data } = await getFlightsBookingEmails(bookingId, { page, page_size: 1000 })
          if (requestId !== this.requestId) return
          const results = Array.isArray(data) ? data : data && data.results
          if (!Array.isArray(results)) throw new Error('Invalid email list response')
          let added = 0
          results.forEach(email => {
            if (!emails.has(email.id)) added++
            emails.set(email.id, email)
          })
          hasNext = !Array.isArray(data) && Boolean(data.next)
          if (hasNext && !added) throw new Error('Email list did not advance')
          page++
        }
        this.emails = Array.from(emails.values()).sort((a, b) => b.id - a.id)
        this.loaded = true
      } catch (error) {
        if (requestId === this.requestId) this.error = responseError(error, 'Unable to load e-mails')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-emails { min-width: 0; }
.booking-emails__toolbar { display: flex; justify-content: flex-start; margin-bottom: 16px; }
.booking-emails__list { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; margin: 0; padding: 0; list-style: none; }
.booking-emails__card { min-width: 0; overflow: hidden; border: 1px solid #dce0d6; border-radius: 8px; background: #fff; }
.booking-emails__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 10px 16px;
  border-bottom: 1px solid #dce0d6;
  background: #fafafa;
  h3 { display: flex; align-items: center; gap: 10px; min-width: 0; margin: 0; font-size: 15px; font-weight: 500; overflow-wrap: anywhere; }
  h3 > .pi { flex-shrink: 0; color: #62685d; }
  time { color: #62685d; font-size: 13px; }
}
.booking-emails__meta { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px 12px; margin-left: auto; }
.booking-emails__sender { min-width: 0; overflow-wrap: anywhere; font-size: 13px; }
.booking-emails__body { padding: 12px 16px; }
.booking-emails__facts { margin: 0; font-size: 14px; }
.booking-emails__subject { display: flex; align-items: baseline; gap: 8px; }
.booking-emails__facts dt { flex-shrink: 0; color: #62685d; }
.booking-emails__facts dd { min-width: 0; margin: 0; overflow-wrap: anywhere; font-weight: 500; }
.booking-emails__badge { display: inline-flex; padding: 3px 8px; border-radius: 4px; background: #f7f8f5; color: #62685d; font-size: 13px; }
.booking-emails__badge[data-state='yes'] { background: #f0f6e7; color: #527b12; }
.booking-emails__badge[data-state='no'] { background: #fff0ef; color: #b03932; }
.booking-emails__badge[data-state='unknown'] { background: #e8ebe3; }
.booking-emails__muted { color: #62685d; font-size: 13px; }
.booking-emails__table-scroll { overflow-x: auto; }
.booking-emails table { width: 100%; border-collapse: collapse; font-size: 13px; }
.booking-emails th, .booking-emails td { padding: 8px 16px; border-top: 1px solid #dce0d6; text-align: left; }
.booking-emails th { background: #fafafa; color: #62685d; font-weight: 500; white-space: nowrap; }
.booking-emails__column-total { margin-left: 4px; color: #20241d; font-weight: 500; font-variant-numeric: tabular-nums; }
.booking-emails td:first-child { min-width: 180px; overflow-wrap: anywhere; }
.booking-emails td time { white-space: nowrap; }
.booking-emails__message { margin: 0; padding: 18px; color: #62685d; font-size: 14px; }
.booking-emails__error { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 18px; color: #b03932; }
.booking-emails__sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
</style>
